import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import { uniq } from 'lodash';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { Container, Paper, TextField, Typography } from '@material-ui/core';
import ImageSearchIcon from '@material-ui/icons/ImageSearch';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import ReplayIcon from '@material-ui/icons/Replay';
import { DropContext } from '../context/DropContext';
import Uploader from '../components/Uploader';
import Footer from '../components/Footer';
import { ReactComponent as LumioLogo } from '../assets/img/lumio-drop.svg';
import { ReactComponent as LumioLogoMobile } from '../assets/img/lumio-drop-mobile.svg';

const useStyles = makeStyles((theme) => ({
    dropWrapper: {
        height: "auto",
        display: "flex",
        alignItems: "center",
        flexDirection: "column"
    },
    dropContainer: {
        paddingTop: 16,
        paddingBottom: 16,
        minHeight: "calc(100vh - 60px)",
        height: "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        [theme.breakpoints.up('md')]: {
            paddingTop: 20,
            paddingBottom: 20,
        },
        [theme.breakpoints.up('xl')]: {
            paddingTop: 40,
            paddingBottom: 40,
        },
    },
    dropPaper: {
        margin: "16px 0 32px",
    },
    dropArea: {
        height: "auto"
    },
    errorMessageCountFiles: {
        margin: "12px 24px"
    },
    form: {
        padding: 20
    },
    bottomContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end"
    },
    bottomActionContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        marginLeft: 16
    },
    buttonRetry: {
    },
    buttonTransfer: {
    },
    buttonCancelAddMore: {
        color: theme.palette.error.main
    },
    buttonBrowse: {
        backgroundColor: "#FFFFFF",
        color: theme.custom.colors.fontDefault,
        '&:hover': {
            backgroundColor: "#e8eaf6",
        }
    },
    lumioLogoContainer: {
        marginBottom: 20,
        [theme.breakpoints.up(1441)]: {
            marginBottom: 40
        },
    },
    lumioLogo: {
        display: "none",
        width: 100,
        marginLeft: -34,
        height: 50,
        [theme.breakpoints.up('lg')]: {
            display: "block",
        },
        [theme.breakpoints.up('xl')]: {
            display: "block",
        },
    },
    lumioLogoMobile: {
        display: "block",
        width: 100,
        marginLeft: -34,
        height: 50,
        [theme.breakpoints.up('lg')]: {
            display: "none"
        },
    },
}));

const Drop = () => {
    const classes = useStyles();
    const history = useHistory();
    const isInit = useRef(true);
    const {druid, clearDruid, generateDruid} = useContext(DropContext);

    const [browseButton, setBrowseButton] = useState(true);

    // Total of how may files is added
    const [countFiles, setCountFiles] = useState(0);
    const [countFilesError, setCountFilesError] = useState({isError: false, errorMessage: "Please include at least 1 file to transfer"});
    
    // Store description
    const [description, setDescription] = useState("");
    const [descriptionError, setIsDescriptionError] = useState({isError: false, errorMessage: ""});

    // Render bottom action buttons (Transfer & Retry Button)
    const [transferButtonState, setTransferButtonState] = useState({isTransfer: false, isDisabled: false, label: "Start Transfer"});
    const [isRetry, setIsRetry] = useState(false);

    // Store uppy all  Files that has beed added/dropped 
    // (complete means the file is already added. It can be successful or failed).
    const [allCompleteFiles, setAllCompleteFiles] = useState({failed: [], successful: [], uploadID: ""});
    
    // Store the latest response from uppy,
    // Uppy response doesn't include any the previous files which already succesful,
    // So we stored previous files in allCompleteFiles, make sure to merge allCompleteFiles with this
    const [completeFiles, setCompleteFiles] = useState({failed: [], successful: [], uploadID: ""});
    
    // Everytime user remove file from list, store it to state
    // then remove the selected file in AllCompleteFiles
    const [removedFile, setRemovedFile] = useState(null);

    const [isDirty, setIsDirty] = useState(false);
    const [isAllFinished, setIsAllFinished] = useState(false);


    useEffect(() => {
        if (isDirty) {
            window.onbeforeunload = (event) => {
                event.returnValue = `Are you sure you want to leave? Changes you made may not be saved.`;
            };
        }
    }, [isDirty]);

    useEffect(() => {
        if (isAllFinished) {
            window.onbeforeunload = null;
            history.push(`/d/${druid}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAllFinished]);

    useEffect(() => {
        if (isInit.current) {
            setIsDescriptionError({isError: false, errorMessage: ""});
            return;
        }
        // Uncomment validation is needed
        // validateDescription(description);
    }, [description]);

    useEffect(() => {
        if (isInit.current) {
            return;
        }
        validateCountFiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countFiles]);

    useEffect(() => {
        clearDruid();

        if (isInit.current) {
            isInit.current = false;
            setIsDirty(false);
            setTransferButtonState({
                isTransfer: false,
                isDisabled: false,
                label: "Start Transfer"
            });
            setBrowseButton(true);
            return;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Uncomment if you need to validate description
    // const validateDescription = (value) => {
    //     if (value.length < 15) {
    //         setIsDescriptionError({
    //             isError: true,
    //             errorMessage: "Message should be at least 15 characters in length"
    //         })
    //         return false;
    //     }
    //     setIsDescriptionError({
    //         isError: false,
    //         errorMessage: ""
    //     });
    //     return true;
    // };

    const validateCountFiles = () => {
        if (countFiles <= 0) {
            setCountFilesError({
                ...countFilesError,
                isError: true,
            });

            if (transferButtonState.isTransfer) {
                changeTransferButtonState(false);
            }
            return false;
        }

        setCountFilesError({
            ...countFilesError,
            isError: false,
        });

        if (!isDirty) {
            setIsDirty(true);
        }
        return true;
    };

    const changeTransferButtonState = useCallback((isTransfer) => {
        if (isTransfer) {
            setTransferButtonState({
                isTransfer: true,
                isDisabled: true,
                label: "Processing..."
            });
            setBrowseButton(false);
            return;
        } else {
            setTransferButtonState({
                isTransfer: false,
                isDisabled: false,
                label: "Start Transfer"
            });
            setBrowseButton(true);
            setIsRetry(false);
        }
    }, []);

    // Uncomment if when retry state allow to edit description,
    // make sure to request new DRUID with the new description,
    // then call the transfer event
    /*
    useEffect(() => {
        if (isRetry) {
            changeTransferButtonState(false);
        } else {
            changeTransferButtonState(true);
        }
    },[isRetry]);
    */

    const onChangeDescription = (e) => {
        setDescription(e.target.value);
    };

    const handleClickTransfer = (e) => {
        // const isValidatedDescription = validateDescription(description);
        const isValidatedCountFiles = validateCountFiles();
        
        if (isValidatedCountFiles) {
            // changeTransferButtonState(true);

            async function getNewDruid() {
                generateDruid(description);
            }
            getNewDruid();
        }
    };

    const handleClickRetry = (e) => {
        const elButton = document.getElementsByClassName("uppy-StatusBar-actionBtn--retry");
        if (elButton[0] !== undefined) {
            elButton[0].click();
            changeTransferButtonState(true);
            setIsRetry(false);
        }
    };

    const handleClickBrowseFiles = () => {
        const elButton = document.getElementsByClassName("uppy-DragDrop-input");
        if (elButton[0] !== undefined) {
            elButton[0].click();
        }
    };

    // Called everytime <Uploader/> return complete (successful & failed) files
    // If there's an error/failed then user retry the upload,
    // the previous complete-successful files is not included (Uppy behavior)
    // so we need manually stored all Files
    useEffect(() => {
        // For succesful files,
        const currentCompleteFilesSuccessful = [...allCompleteFiles.successful];
        const completeFilesSuccesful = completeFiles.successful;
        const allCompleteFilesSuccessful = uniq(currentCompleteFilesSuccessful.concat(completeFilesSuccesful)); 
        
        // For failed files, just replace allCompleteFilesFailed with the new one
        const allCompleteFilesFailed = [...completeFiles.failed];

        setAllCompleteFiles({
            failed: allCompleteFilesFailed,
            successful: allCompleteFilesSuccessful
        });

        // Check if allCompleteFiles which failed is gone (if retry) or all files is successful in first try
        if (allCompleteFilesFailed.length === 0 && allCompleteFilesSuccessful.length > 0) {
            setIsAllFinished(true);
        } else if (allCompleteFilesFailed.length === 0 && allCompleteFilesSuccessful.length === 0) {
            changeTransferButtonState(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [completeFiles]);

    // Listen everytime file removed from <Uploader/>
    useEffect(() => {
        if (allCompleteFiles.failed.length !== 0) {
            const newAllCompleteFilesFailed = allCompleteFiles.failed.filter((item) => {
                return item.id !== removedFile.id
            });
            console.log(newAllCompleteFilesFailed);
            setCompleteFiles({...allCompleteFiles, failed: newAllCompleteFilesFailed});
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[removedFile]);

    return(
        <div className={classes.dropWrapper}>
            <Container maxWidth="md" className={classes.dropContainer}>
                <div className={classes.lumioLogoContainer}>
                    <RouterLink to="/" style={{display:"table"}}>
                        <LumioLogo className={classes.lumioLogo}/>
                        <LumioLogoMobile className={classes.lumioLogoMobile}/>
                    </RouterLink>
                </div> 
                 
                <Prompt
                    when={isDirty && !isAllFinished}
                    message={location => `Are you sure you want leave? Changes you made may not be saved.`}
                />
                <div>
                    <div>
                        <Button variant="contained" disableElevation={true} className={classes.buttonBrowse} disabled={!browseButton} onClick={handleClickBrowseFiles} size={"medium"} startIcon={<ImageSearchIcon fontSize="small"/>}>
                            Browse Files
                        </Button>
                    </div>
                </div>

                <Paper elevation={2} className={classes.dropPaper}>
                    <div className={classes.dropArea}>
                        <Uploader 
                            druid={druid}
                            changeTransferButtonState={changeTransferButtonState}
                            setIsRetry={setIsRetry}
                            setCountFiles={setCountFiles} 
                            setCompleteFiles={setCompleteFiles}
                            setRemovedFile={setRemovedFile}
                        />
                    </div>

                    {
                        countFilesError.isError &&
                        <div className={classes.errorMessageCountFiles}>
                            <Typography variant="body2" color="error">{countFilesError.errorMessage}</Typography>
                        </div>
                    }

                    <form className={classes.form} noValidate autoComplete="off">
                        <TextField 
                            fullWidth
                            label="Additional Message" 
                            placeholder="Please tell us more about the files..."
                            variant="outlined" 
                            multiline
                            minRows={2}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            InputProps={{
                                readOnly: transferButtonState.isDisabled,
                            }}
                            onChange={onChangeDescription}
                            error={descriptionError.isError}
                            helperText={descriptionError.errorMessage}
                        />
                    </form>
                </Paper>

                <div className={classes.bottomContainer}>
                    {
                        isRetry &&
                        <div>
                            <Typography align="right" variant="body2" color="error">
                                Failed to transfer files. This looks like a network error,
                                <br/>
                                the endpoint might be blocked by an internet provider or a firewall.
                            </Typography>
                        </div>
                    }
                    
                    <div className={classes.bottomActionContainer}>
                        {
                            isRetry &&
                            <Button 
                                variant="contained" 
                                color="secondary" 
                                size="large"
                                className={classes.buttonRetry}
                                onClick={handleClickRetry}
                                startIcon={<ReplayIcon fontSize="small"/>}
                            >
                                Retry
                            </Button>
                        }
                        {
                            !isRetry &&
                            <Button 
                                variant="contained" 
                                color="primary" 
                                size="large"
                                className={classes.buttonTransfer}
                                disabled={transferButtonState.isDisabled}
                                onClick={handleClickTransfer}
                                startIcon={<CloudUploadIcon fontSize="small"/>}
                            >
                                {transferButtonState.label}
                            </Button>
                        }

                        
                    </div>
                    
                </div>
            </Container>

            <Footer />
        </div>
    );
};

export default Drop;